import * as React from "react";

import { observer, inject } from "mobx-react";

import { ConfigurationStore } from "src/stores/ConfigurationStore";
import { HorseForSaleStore } from "src/stores/HorseForSaleStore";
import { HorseForSaleListStore } from "src/stores/HorseForSaleListStore";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";

import LoadingWidget from "../common/LoadingWidget";
import { CustomerLink } from "../common/CustomerLink";
import CustomerMultiselect from "../common/CustomerMultiselect";
import { SelectGroup } from "../common/SimpleFormComponents";
import { HorseLink } from "../common/HorseLink";
import { successMessage, errorMessage } from "../common/Toast";

import {
    SimpleHorseForSale,
    HorseForSale,
    HorseForSaleState,
    HorseSale,
    HorseForSaleStateFriendlyName,
    AllPostApprovedStates
} from "src/model/HorseForSale";
import { SimpleCustomer, ExtractFriendlyName } from "src/model/Customer";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { UncontrolledTooltip } from "reactstrap";

interface QuickAgriplexEditProps {
    horseForSale: SimpleHorseForSale;
    onChange: (horseForSale: SimpleHorseForSale, value: string) => Promise<any>;
}
class QuickAgriplexEdit extends React.Component<
    QuickAgriplexEditProps,
    {
        value: string;
        loading: boolean;
    }
> {
    constructor(props: QuickAgriplexEditProps) {
        super(props);
        this.state = {
            value: props.horseForSale.spotInAgriplex || "",
            loading: false
        };
    }

    update = async () => {
        if (this.props.horseForSale.spotInAgriplex === this.state.value) {
            return;
        }
        try {
            this.setState({ loading: true });
            await this.props.onChange(this.props.horseForSale, this.state.value);
        } finally {
            this.setState({ loading: false });
        }
    };

    render() {
        const warning = (this.props.horseForSale.spotInAgriplex || "") !== this.state.value;
        return (
            <div className="input-group">
                <input
                    className={`form-control ${warning ? "border-warning" : ""}`}
                    type="text"
                    value={this.state.value}
                    placeholder="Click to add..."
                    onChange={e => {
                        e.preventDefault();
                        this.setState({ value: e.target.value });
                    }}
                    disabled={this.state.loading}
                    onBlur={this.update}
                />
            </div>
        );
    }
}

const DEFAULT_STATES = AllPostApprovedStates();

export interface Props {
    configurationStore?: ConfigurationStore;
    horseForSaleListStore?: HorseForSaleListStore;
    horseForSaleStore?: HorseForSaleStore;
    customerEndpoints?: CustomerEndpoints;
}

export interface State {
    spotInAgriplex: string;
    selectedSale: HorseSale | null;
    selectedHorsesForSale: Array<HorseForSale>;
    consignors: Array<SimpleCustomer> | null;
}

@inject("customerEndpoints")
@inject("horseForSaleListStore")
@inject("horseForSaleStore")
@inject("configurationStore")
@observer
export class AssignSpotAgriplex extends React.Component<Props, State> {
    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
        this.state = {
            spotInAgriplex: "",
            selectedHorsesForSale: [],
            consignors: null,
            selectedSale: null
        };
    }

    onChangeSelectConsignor = (selectedConsignors: Array<SimpleCustomer>): void => {
        this.props.horseForSaleListStore!.updateHorseForSaleListFilters({ selectedConsignors });
    };

    onChangeSelectSale = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedSaleId = e.target.value === "" ? null : parseInt(e.target.value, 10);
        const selectedSale = selectedSaleId ? this.props.configurationStore!.findHorseSaleById(selectedSaleId) : null;

        this.setState({ selectedSale });
        this.props.horseForSaleListStore!.updateListState({ sizePerPage: 1000 }, false);
        this.props.horseForSaleListStore!.updateListFilters({
            selectedSales: selectedSale !== null ? [selectedSale] : [],
            selectedStates: DEFAULT_STATES,
            selectedConsignors: []
        });
    };

    updateArgriplexSpot = async (horseForSale: SimpleHorseForSale, spotName: string) => {
        try {
            // const goAhead = await SimpleConfirmation(`Are you sure you'd like to assign ${spotName} to ${horseForSale.horse.name}`);
            // if (!goAhead) { return; }
            // TODO: Just go straight to the store (we're not observing anything here in the UI, so we don't really need it)
            await this.props.horseForSaleStore!.horseForSaleUpdateFieldsByObject(horseForSale.id, { spotinagriplex: spotName });
            successMessage("Changes successfully saved.");
            this.props.horseForSaleListStore!.refresh(false);
        } catch (error) {
            errorMessage(error);
        }
    };

    applySpotToConsignor = async (horseForSale: SimpleHorseForSale, spotName: string) => {
        try {
            const horseForSaleListStore = this.props.horseForSaleListStore;
            const results = horseForSaleListStore!.horseForSaleList.results;

            const goAhead = await SimpleConfirmation(
                `Are you sure you'd like to assign ${spotName}
                to all horses for sale for ${horseForSale.consignor && ExtractFriendlyName(horseForSale.consignor)}?`
            );
            if (!goAhead) {
                return;
            }

            const consignorId = horseForSale.consignor!.id;
            const hfsSameConsignor = results.filter(
                hfs =>
                    hfs.consignor!.id === consignorId &&
                    (hfs.state === HorseForSaleState.APPROVED || hfs.state === HorseForSaleState.APPROVED_FOR_SALE)
            );

            await Promise.all(
                hfsSameConsignor.map(async hfs => {
                    await this.props.horseForSaleStore!.horseForSaleUpdateFieldsByObject(hfs.id, {
                        spotinagriplex: spotName
                    });
                })
            );
            successMessage("Changes successfully saved.");

            this.props.horseForSaleListStore!.reset();
            this.props.horseForSaleListStore!.updateListState(
                {
                    sizePerPage: 1000,
                    sortField: "consignor",
                    sortOrder: "asc"
                },
                false // Don't refresh because the next operation will handle that
            );
            this.props.horseForSaleListStore!.updateListFilters({
                selectedSales: this.state.selectedSale ? [this.state.selectedSale] : [],
                selectedStates: DEFAULT_STATES,
                selectedConsignors: this.state.consignors ? this.state.consignors : []
            });
        } catch (error) {
            errorMessage(error);
        }
    };

    componentDidMount() {
        // We avoid refreshing here to avoid unnecessary network chatter
        this.props.horseForSaleListStore!.reset();
        this.props.horseForSaleListStore!.updateListState({ sizePerPage: 1000, sortField: "consignor", sortOrder: "asc" }, false);
        this.props.horseForSaleListStore!.updateListFilters(
            {
                selectedSales: [],
                selectedStates: DEFAULT_STATES
            },
            false
        );
    }

    clearList = () => {
        this.setState({
            spotInAgriplex: "",
            selectedHorsesForSale: []
            // consignors: null
            // selectedSale: null
        });
    };

    handleOnChange = (event: React.FormEvent<HTMLInputElement>) => {
        const target = event.currentTarget;
        const value = target.value;
        const name = target.name;

        const state = this.state;
        state[name] = value;
        this.setState(state);
    };

    render() {
        const configurationStore = this.props.configurationStore;
        if (!configurationStore) {
            return <div>No Configuration Store found.</div>;
        }
        const horseForSaleListStore = this.props.horseForSaleListStore;
        if (!horseForSaleListStore) {
            return <div>No Horse For Sale List Store found.</div>;
        }
        const customerEndpoints = this.props.customerEndpoints;
        if (!customerEndpoints) {
            return <div>No HcustomerEndpoints found.</div>;
        }

        // Include all horses for sale in the active states that the store is already filtered to
        // *except* any horse that has been withdrawn-without-penalty w/o a hip number (implying they were
        // withdrawn before the hip numbers were generated)
        const results = this.state.selectedSale
            ? horseForSaleListStore.horseForSaleList.results.filter(
                  hfs => !(hfs.hipNumber === null && hfs.state === HorseForSaleState.WITHDRAWN_WITHOUT_PENALTY)
              )
            : [];

        const horseForSaleListFilters = horseForSaleListStore.horseForSaleListFilters;

        return (
            <div>
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item active" aria-current="page">
                            Assign Spot in the Agriplex
                        </li>
                    </ol>
                </nav>
                <div className="content">
                    <div className="row">
                        <div className="col-12">
                            <h1>Assign Spot in the Agriplex</h1>
                            <div className="card shadow">
                                <div className="card-body">
                                    <SelectGroup
                                        label="Select Sale:"
                                        name="documentType"
                                        isRequired={true}
                                        className={`col-md-12 mx-0 px-0`}
                                        disabled={false}
                                        value={this.state.selectedSale ? "" + this.state.selectedSale.id : ""}
                                        onChange={e => this.onChangeSelectSale(e)}
                                    >
                                        <option value=""> Select sale...</option>
                                        {configurationStore!.horseSales
                                            .sort((a: HorseSale, b: HorseSale) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                            .map((c: HorseSale) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name}
                                                </option>
                                            ))}
                                    </SelectGroup>
                                    <div className="card mb-5">
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-12">
                                                    <label htmlFor="consignor">Consignor</label>
                                                    <CustomerMultiselect
                                                        customerEndpoints={this.props.customerEndpoints!}
                                                        include="BOTH"
                                                        involvementSaleFilters={horseForSaleListFilters.selectedSales.map(s => s.id)}
                                                        involvementRoleFilters={["consignor"]}
                                                        value={horseForSaleListFilters.selectedConsignors}
                                                        onChange={selectedConsignors => {
                                                            this.onChangeSelectConsignor(selectedConsignors);
                                                            this.setState({ consignors: selectedConsignors });
                                                        }}
                                                        showCreate={false}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>Hip #</th>
                                                    <th>Horse Name</th>
                                                    <th>Consignor</th>
                                                    <th>Sale State</th>
                                                    <th>Spot in the Agriplex</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {results.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={5} align="center">
                                                            {horseForSaleListStore.loading ? <LoadingWidget /> : <span>No results found.</span>}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    results.map(hfs => {
                                                        let classes = "";
                                                        if (
                                                            hfs.state === HorseForSaleState.WITHDRAWN_WITH_PENALTY_GENERATED ||
                                                            hfs.state === HorseForSaleState.WITHDRAWN_WITH_PENALTY_PENDING ||
                                                            hfs.state === HorseForSaleState.WITHDRAWN_WITHOUT_PENALTY
                                                        ) {
                                                            classes += "row-warning";
                                                        }
                                                        return (
                                                            <tr key={hfs.id} className={classes ? classes : ""}>
                                                                <td>{hfs.hipNumber}</td>
                                                                <td>
                                                                    <HorseLink horse={hfs.horse} targetBlank={true} />
                                                                </td>
                                                                <td>
                                                                    <CustomerLink customer={hfs.consignor} targetBlank={true} />
                                                                </td>
                                                                <td>{HorseForSaleStateFriendlyName(hfs.state)}</td>
                                                                <td style={{ minWidth: "250px" }}>
                                                                    {hfs.state === HorseForSaleState.APPROVED ||
                                                                    hfs.state === HorseForSaleState.APPROVED_FOR_SALE ? (
                                                                        <div className="d-flex">
                                                                            <QuickAgriplexEdit
                                                                                horseForSale={hfs}
                                                                                onChange={this.updateArgriplexSpot}
                                                                            />

                                                                            <button
                                                                                className="d-block btn btn-primary btn-sm ml-2"
                                                                                id={`spotInAgriplexIdToConsignor_${hfs.id}`}
                                                                                style={{ paddingTop: "7px", paddingBottom: "7px" }}
                                                                                onClick={_ =>
                                                                                    this.applySpotToConsignor(hfs, hfs.spotInAgriplex || "")
                                                                                }
                                                                                disabled={
                                                                                    this.props.horseForSaleStore!.loading ||
                                                                                    this.props.horseForSaleListStore!.loading
                                                                                }
                                                                            >
                                                                                <FontAwesomeIcon icon={faCopy} />
                                                                            </button>
                                                                            <UncontrolledTooltip
                                                                                placement="top"
                                                                                target={`spotInAgriplexIdToConsignor_${hfs.id}`}
                                                                                delay={0}
                                                                                className="tooltipstrap"
                                                                            >
                                                                                Add same spot to the same Consignor
                                                                            </UncontrolledTooltip>
                                                                        </div>
                                                                    ) : (
                                                                        <span>{hfs.spotInAgriplex ? hfs.spotInAgriplex : "-"}</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                        {horseForSaleListStore.loading && <div />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/horses-for-sale/AssignSpotAgriplex.tsx